import ShadowContainerHorizontal from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import Tag from '@atoms/Tag/Tag';
import { IMeta } from '@models/IMeta';
import ITabbedProductListingItem from '@models/ITabbedProductListingItem';
import TagTypesEnum from '@models/enums/TagTypesEnum';
import ProductCarousel from '@organisms/ProductCarousel/ProductCarousel';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

interface Props {
  readonly anchorId?: string;
  readonly items: ITabbedProductListingItem[];
  readonly meta?: IMeta;
  readonly title: string;
}

const TabbedProductListing = ({ anchorId = '', items, meta, title }: Props) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const displayedTabs =
    items?.filter((tab) => tab.title && !!tab.items?.length) ?? [];
  const tabsTitles = displayedTabs.map((tab) => tab.title) ?? [];
  const tabsContainer = (
    <ShadowContainerHorizontal
      threshold={0.01}
      hasTabbableNestedItems
      className="min-w-0"
    >
      <div className="flex gap-x-5">
        {tabsTitles.map((tabTitle, index) => {
          const isActive = index === activeTabIndex;

          return (
            <Tag
              key={tabTitle}
              text={tabTitle}
              tagType={isActive ? TagTypesEnum.Secondary : TagTypesEnum.Primary}
              pointerStates={!isActive}
              disabled={isActive}
              onClick={() => setActiveTabIndex(index)}
            />
          );
        })}
      </div>
    </ShadowContainerHorizontal>
  );

  return (
    <section
      className="component-padding bg-white text-black-100 dark:bg-black-100 dark:text-white"
      id={`a-${anchorId}`}
    >
      {title && <h2 className="container mb-7.5 lg:mb-10">{title}</h2>}

      {!!displayedTabs?.length && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{
              opacity: 0,
              transition: {
                duration: 0,
              },
            }}
            key={`${tabsTitles[activeTabIndex]}-${activeTabIndex}`}
          >
            <ProductCarousel
              embedded
              meta={meta ?? {}}
              items={displayedTabs[activeTabIndex]!.items}
              headerWrapperClassName="xl:min-h-[2.5rem] gap-x-5 xl:gap-x-20"
              headerSlot={tabsContainer}
            />
          </motion.div>
        </AnimatePresence>
      )}
    </section>
  );
};

export default TabbedProductListing;
